import { TICKER_ITEMS, TOP_RISING, FEATURED } from '@/mocks/data';

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

const rankColor = (rank: number) => {
  if (rank === 1) return 'bg-primary-500 text-background-50';
  if (rank === 2) return 'bg-accent-500 text-foreground-950';
  if (rank === 3) return 'bg-accent-300 text-accent-950';
  return 'bg-background-200 text-foreground-600';
};

const deltaStyle = (d: string) => {
  if (d === '爆') return 'bg-primary-100 text-primary-700';
  if (d === '新') return 'bg-secondary-100 text-secondary-700';
  return 'bg-accent-100 text-accent-800';
};

export default function Hero() {
  return (
    <section id="top" className="pt-16 md:pt-[72px]">
      {/* live ticker */}
      <div className="flex items-stretch overflow-hidden bg-primary-900">
        <div className="z-10 flex shrink-0 items-center gap-1.5 bg-primary-700 px-4 py-2 text-background-50 md:px-5">
          <i className="ri-flashlight-fill animate-pulseDot text-accent-300"></i>
          <span className="text-xs font-bold tracking-wide md:text-sm">全网热点</span>
        </div>
        <div className="relative flex flex-1 items-center overflow-hidden">
          <div className="flex w-max animate-marquee items-center">
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
              <span key={i} className="flex items-center whitespace-nowrap px-6 text-sm text-background-100/90">
                <i className="ri-arrow-right-s-fill mr-1 text-accent-400"></i>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* featured */}
          <article className="group relative flex min-h-[440px] cursor-pointer flex-col justify-end overflow-hidden rounded-2xl lg:col-span-8 md:min-h-[560px]">
            <img
              src={FEATURED.image}
              alt="演唱会与暑期档消费热潮题材的编辑风格配图"
              className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10"></div>
            <button
              type="button"
              onClick={() => scrollToId('feed')}
              className="absolute right-4 top-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/30 text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:right-5 md:top-5"
              aria-label="阅读头条详情"
            >
              <i className="ri-arrow-right-up-line text-lg"></i>
            </button>

            <div className="relative z-10 p-5 md:p-8">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  <i className="ri-fire-fill text-accent-300"></i>
                  {FEATURED.tag}
                </span>
                <span className="rounded-full bg-black/30 px-3 py-1 text-xs font-medium text-background-100 backdrop-blur">
                  {FEATURED.cat}
                </span>
              </div>
              <h1 className="font-heading text-2xl font-black leading-snug text-background-50 text-balance md:text-[34px]">
                {FEATURED.title}
              </h1>
              <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-100/90 sm:block md:text-base">
                {FEATURED.summary}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-200 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {FEATURED.time}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {FEATURED.views} 热度
                </span>
              </div>
            </div>
          </article>

          {/* live ranking */}
          <aside className="lg:col-span-4">
            <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3.5">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-bar-chart-2-fill text-primary-600"></i>
                  实时飙升榜
                </h3>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-secondary-700">
                  <i className="ri-record-circle-fill animate-pulseDot"></i>
                  LIVE
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-1 p-2 md:gap-1.5">
                {TOP_RISING.map((item) => (
                  <button
                    key={item.rank}
                    type="button"
                    onClick={() => scrollToId('rank')}
                    className="group flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-background-50"
                  >
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-black ${rankColor(item.rank)}`}>
                      {item.rank}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                        <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${deltaStyle(item.delta)}`}>{item.delta}</span>
                        热度 {(item.heat / 10000).toFixed(1)}万
                      </span>
                    </span>
                    <i className="ri-arrow-right-line mt-1 text-foreground-300 opacity-0 transition-opacity group-hover:opacity-100"></i>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => scrollToId('rank')}
                className="cursor-pointer border-t border-background-200 px-4 py-3 text-center text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                查看完整今日热榜
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}